/**
 * ActionBarSlot.tsx
 *
 * A single slot in the ActionBar.
 * Handles its own drag-over rendering based on props. Pure visual component.
 */

import React, { useState } from 'react';
import { Ability, Helix2000Character } from '../../../../shared/CharacterTypes';
import type { ItemData } from '../../components/itemkit/shared';
import { CooldownOverlay } from '../../components/CooldownOverlay';
import { Tooltip, createItemTooltip } from '../../components/Tooltip';
import type { TooltipData } from '../../components/Tooltip';
import { ItemIcon } from '../../components/ItemIcon';
import { abilityRegistry } from '../../../systems/player/abilities/AbilityRegistry';
import { abilityTooltipData } from '../../components/abilityTooltip';
import { useAbilityLiveCooldown, useLiveCooldown } from './liveCooldown';
import { useHotbarCooldownNumbers } from './hotbarCooldownNumbersPref';
import { quickKeybindStore, useQuickKeybindSnapshot } from './quickKeybindStore';
import {
  Slot,
  KeyLabel,
  ManaBadge,
  AbilityIcon,
  EmptySlotInner,
  DropTargetCrosshair,
} from './ActionBarStyles';
import { COLORS, FONT_SIZE, FONT_WEIGHT, FONTS, RADII } from '../../theme';

interface ActionBarSlotProps {
  barId?: number;
  page?: number;
  slotIndex: number;
  slotKey: string;
  ability?: Ability;
  item?: ItemData;
  player: Helix2000Character;
  /** True if the ability is uncastable under the active race gate (§5.1). */
  raceDisabled?: boolean;
  /** A locked session bar (D1): slots cannot be dragged. */
  locked?: boolean;
  isDragOver: boolean;
  isGlobalDrag: boolean;
  wasActivated: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

/** Count changes update the badge; animation fractions never render the slot. */
const slotModeKey = (state: { mode: string; charges?: number }): string => `${state.mode}|${state.charges}`;

export const ActionBarSlot: React.FC<ActionBarSlotProps> = ({
  barId = 1,
  page = 1,
  slotIndex,
  slotKey,
  ability,
  item,
  player,
  raceDisabled = false,
  locked = false,
  isDragOver,
  isGlobalDrag,
  wasActivated,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onClick,
  onContextMenu,
}) => {
  const isEmpty = !ability && !item;
  const showCooldownNumbers = useHotbarCooldownNumbers();
  const [isHovered, setIsHovered] = useState(false);
  const { tableHoveredSlot, hudHoveredSlot, quickKeybindActive } = useQuickKeybindSnapshot();

  const isTableHighlighted =
    tableHoveredSlot?.bar === barId && tableHoveredSlot?.slot === slotIndex;
  const isQuickBindHighlighted =
    quickKeybindActive && hudHoveredSlot?.bar === barId && hudHoveredSlot?.slot === slotIndex;
  const isSlotHighlighted = isTableHighlighted || isQuickBindHighlighted;
  const showSlotIndexHint = isHovered || isSlotHighlighted || quickKeybindActive;
  const displayedKeyLabel = slotKey || (showSlotIndexHint ? `#${slotIndex}` : '');

  // ─── Cooldown (read from CooldownStore — survives Colyseus state patches) ─
  // The slot follows mode and charge count; the overlay follows number and pips
  // and moves the sweep itself, so a cooldown never re-renders React per frame.
  const liveCooldown = useAbilityLiveCooldown(ability);
  const cooldown = useLiveCooldown(liveCooldown, slotModeKey);
  const isOnCooldown = !!ability && cooldown.mode === 'cooldown';

  const handleMouseEnter = () => {
    setIsHovered(true);
    quickKeybindStore.setHudHoveredSlot({ bar: barId, slot: slotIndex });
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    const current = quickKeybindStore.getSnapshot().hudHoveredSlot;
    if (current?.bar === barId && current?.slot === slotIndex) {
      quickKeybindStore.setHudHoveredSlot(null);
    }
  };

  const slotEl = (
    <Slot
      className="action-bar-slot hudt-slot"
      data-mode={isEmpty ? 'empty' : isOnCooldown ? 'cooldown' : 'ready'}
      data-empty={isEmpty ? 'true' : 'false'}
      data-bar={barId}
      data-page={page}
      data-slot={slotIndex}
      data-slot-highlighted={isSlotHighlighted ? 'true' : undefined}
      data-ability-id={ability?.id ?? ''}
      data-cooldown={ability ? cooldown.mode : undefined}
      data-item-id={item?.id ?? ''}
      $isEmpty={isEmpty}
      $isOnCooldown={isOnCooldown}
      $isDragOver={isDragOver}
      $isGlobalDrag={isGlobalDrag}
      $wasActivated={wasActivated}
      style={isSlotHighlighted ? highlightedSlotStyle : undefined}
      draggable={!isEmpty && !locked}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      {/* Key binding label (or #slot number when unbound & hovered/highlighted) */}
      <KeyLabel
        style={!slotKey && showSlotIndexHint ? slotNumberFallbackStyle : undefined}
      >
        {displayedKeyLabel}
      </KeyLabel>

      {/* Slot index badge when bound and hovered/highlighted so slot # is always clear */}
      {Boolean(slotKey) && (isSlotHighlighted || (isHovered && quickKeybindActive)) && (
        <div
          data-testid={`action-bar-slot-index-badge-${barId}-${slotIndex}`}
          style={slotIndexBadgeStyle}
        >
          #{slotIndex}
        </div>
      )}

      {isEmpty ? (
        <>
          <EmptySlotInner>
            {isGlobalDrag ? '⬇' : '+'}
          </EmptySlotInner>
          <DropTargetCrosshair $active={isDragOver}>⬇</DropTargetCrosshair>
        </>
      ) : (
        <>
          {/* Ability/item icon — desaturated + 40% alpha while race-disabled (§5.1),
              desaturated and darkened while its cooldown runs. */}
          <AbilityIcon style={raceDisabled
            ? { filter: 'grayscale(1)', opacity: 0.4 }
            : isOnCooldown ? { filter: 'grayscale(0.85) brightness(0.75)' } : undefined}>
            {item ? (
              <ItemIcon itemId={item.id} size={40} />
            ) : ability!.icon?.startsWith('/') ? (
              <img
                src={ability!.icon}
                alt={ability!.name}
                style={{ width: '42px', height: '42px', objectFit: 'contain', pointerEvents: 'none' }}
              />
            ) : (
              ability!.icon || '✨'
            )}
          </AbilityIcon>

          {/* Cooldown sweep overlay */}
          {ability && (
            <CooldownOverlay live={liveCooldown} showNumber={showCooldownNumbers} borderRadius={RADII.lg} />
          )}

          {/* Mana cost badge */}
          {ability && (ability.manaCost || 0) > 0 && (
            <ManaBadge>{ability.manaCost}</ManaBadge>
          )}

          {/* Ability charge count badge (Lanternskip capacity 2) */}
          {ability && cooldown.charges !== undefined && (
            <div
              data-testid={`action-bar-charge-count-${slotIndex}`}
              style={{
                position: 'absolute',
                right: 3,
                bottom: 1,
                zIndex: 5,
                minWidth: 14,
                color: cooldown.charges > 0 ? COLORS.textPrimary : COLORS.danger,
                fontSize: FONT_SIZE.sm,
                fontFamily: FONTS.ui,
                fontWeight: FONT_WEIGHT.bold,
                textAlign: 'right',
                textShadow: '0 1px 2px #000',
                pointerEvents: 'none',
              }}
            >
              {cooldown.charges}
            </div>
          )}

          {item && (
            <div
              className="hudt-cost"
              data-testid={`action-bar-item-count-${slotIndex}`}
              style={{
                position: 'absolute',
                right: 3,
                bottom: 3,
                zIndex: 10,
                minWidth: 18,
                padding: '1px 3px',
                color: item.quantity ? COLORS.textPrimary : COLORS.danger,
                fontSize: FONT_SIZE.xs,
                fontFamily: FONTS.ui,
                fontWeight: FONT_WEIGHT.bold,
                textAlign: 'center',
                textShadow: '0 1px 2px #000',
                pointerEvents: 'none',
              }}
            >
              {item.quantity ?? 0}
            </div>
          )}

          {/* Race-disabled badge — non-kit ability during a GM race (§5.1).
              The icon dim above carries the grey-out, so this is just the
              corner flag. */}
          {raceDisabled && (
            <div
              style={{
                position: 'absolute', top: 1, right: 2, fontSize: 12,
                lineHeight: 1, pointerEvents: 'none', zIndex: 5, opacity: 0.9,
              }}
            >
              🏁
            </div>
          )}

          {/* Drop target crosshair when dragging over a filled slot */}
          <DropTargetCrosshair $active={isDragOver}>⬇</DropTargetCrosshair>
        </>
      )}
    </Slot>
  );

  // Wrap filled slots with a tooltip — pull from registry for full metadata
  if (ability) {
    const meta = abilityRegistry.getMetadata(ability.id);
    // Build extra info from metadata
    const infoParts: string[] = [];
    if (meta?.projectile) infoParts.push(`${meta.projectile.shape || 'projectile'} • speed ${meta.projectile.speed}`);
    if (meta?.element) infoParts.push(`Element: ${meta.element}`);
    if (meta?.isToggle) infoParts.push('Toggle');
    if (meta?.isPassive) infoParts.push('Passive');
    if (meta?.charges) infoParts.push(`Charges: ${meta.charges.maxCharges}`);
    if (meta?.projectile?.modifiers) {
      const mods = meta.projectile.modifiers;
      if (mods.pierce) infoParts.push(`Pierce ×${mods.pierce.count}`);
      if (mods.bounce) infoParts.push(`Bounce ×${mods.bounce.count}`);
      if (mods.chain) infoParts.push(`Chain ×${mods.chain.count}`);
      if (mods.split) infoParts.push(`Split ×${mods.split.count}`);
      if (mods.aoeOnImpact) infoParts.push(`AoE ${mods.aoeOnImpact.radius}px`);
    }

    // Plan 29 F2: the TOML description and a stat line computed from content
    // (damage/heal with the server's formula, reach, radius, cooldown, mana…).
    const tooltipData: TooltipData = abilityTooltipData(ability.id, player, {
      title: ability.name || meta?.name || ability.id,
      fallbackDescription: ability.description || meta?.description,
      extraInfo: infoParts.length > 0 ? infoParts.join(' • ') : undefined,
    });
    return (
      <Tooltip key={slotIndex} data={tooltipData} position="top">
        {slotEl}
      </Tooltip>
    );
  }

  if (item) {
    return (
      <Tooltip
        key={slotIndex}
        data={createItemTooltip(item, {
          level: player.level,
          characterClass: player.class,
        })}
        position="top"
      >
        {slotEl}
      </Tooltip>
    );
  }

  return slotEl;
};

const highlightedSlotStyle: React.CSSProperties = {
  outline: '2px solid #ffd86b',
  outlineOffset: '1px',
  boxShadow: '0 0 12px rgba(255, 216, 107, 0.85), inset 0 0 8px rgba(255, 216, 107, 0.35)',
  zIndex: 6,
};

const slotNumberFallbackStyle: React.CSSProperties = {
  color: 'rgba(240, 230, 210, 0.72)',
  fontSize: '9px',
};

const slotIndexBadgeStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 2,
  left: 3,
  zIndex: 7,
  padding: '0 3px',
  borderRadius: 3,
  background: 'rgba(18, 28, 17, 0.88)',
  border: '1px solid rgba(255, 216, 107, 0.7)',
  color: '#ffd86b',
  fontSize: '9px',
  fontFamily: FONTS.ui,
  fontWeight: FONT_WEIGHT.bold,
  lineHeight: 1.2,
  pointerEvents: 'none',
};
